import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// SCRUM-1943 — an optional tap action makes a toast clickable (e.g. a
// "report ready" toast that navigates to the report on tap).
export interface NotificationAction {
  /** Router commands passed to Router.navigate() when the toast body is tapped. */
  route: string[];
}

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warn' | 'info';
  message: string;
  duration?: number; // milliseconds, undefined = no auto-close
  action?: NotificationAction; // SCRUM-1943 — tap-to-navigate
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly notifications$ = new BehaviorSubject<Notification[]>([]);

  getNotifications(): Observable<Notification[]> {
    return this.notifications$.asObservable();
  }

  /**
   * Show success notification
   */
  success(message: string, duration: number = 3000): void {
    this.show({
      type: 'success',
      message,
      duration,
    });
  }

  /**
   * Show error notification
   */
  error(message: string, duration?: number): void {
    this.show({
      type: 'error',
      message,
      duration: duration || 5000,
    });
  }

  /**
   * Show warning notification
   */
  warning(message: string, duration: number = 4000): void {
    this.show({
      type: 'warn',
      message,
      duration,
    });
  }

  /**
   * Show info notification
   */
  info(message: string, duration: number = 3000): void {
    this.show({
      type: 'info',
      message,
      duration,
    });
  }

  /**
   * SCRUM-1943 — show a fully-specified notification. Used for actionable
   * toasts (e.g. a clickable "report ready" toast with a navigation action).
   * Auto-dismiss still honours `duration`.
   */
  notifyWith(notification: Omit<Notification, 'id'>): void {
    this.show(notification);
  }

  /**
   * Show a notification
   */
  private show(notification: Omit<Notification, 'id'>): void {
    const id = `notification-${Date.now()}-${Math.random()}`; // NOSONAR S2245 — non-security id
    const newNotification: Notification = {
      id,
      ...notification,
    };

    const current = this.notifications$.value;
    this.notifications$.next([...current, newNotification]);

    // Auto-remove if duration is specified
    if (newNotification.duration) {
      setTimeout(() => {
        this.remove(id);
      }, newNotification.duration);
    }
  }

  /**
   * Remove a notification by ID
   */
  remove(id: string): void {
    const current = this.notifications$.value;
    this.notifications$.next(current.filter((n) => n.id !== id));
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notifications$.next([]);
  }
}
